/**
 * TariffComparisonChart Component (was AgileComparisonChart)
 *
 * Line chart showing monthly cost under the current fixed tariff vs an
 * alternative tariff (Agile, Trio, etc). Toggle switches the y-axis
 * between monthly cost (two lines + red/green delta fill) and monthly
 * kWh consumption (single line).
 *
 * Expects normalised data from useTariffComparison (alt_* columns).
 */

import { useMemo, useState } from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import TariffSelector from '@/components/energy/TariffSelector'
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'

const FIXED_COLOR = '#f59e0b'  // amber
const ALT_COLOR = '#0ea5e9'    // sky blue
const SAVING_COLOR = '#22c55e' // green — alternative cheaper
const DEFICIT_COLOR = '#ef4444' // red — alternative more expensive

function formatMonth(month) {
  return new Date(month).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })
}

function formatCurrency(value) {
  if (value == null) return '—'
  if (Math.abs(value) >= 1000) return `£${(value / 1000).toFixed(1)}k`
  return `£${value.toFixed(0)}`
}

function formatKwh(value) {
  if (value == null) return '—'
  if (value >= 1000) return `${(value / 1000).toFixed(1)}k`
  return `${value.toFixed(0)}`
}

function formatUnit(value) {
  if (value == null) return '—'
  return `${value.toFixed(1)}p`
}

function UnitRateTooltip({ active, payload, label, tariffLabel }) {
  if (!active || !payload?.length) return null
  const row = payload[0]?.payload
  if (!row) return null

  const monthYear = new Date(label).toLocaleDateString('en-GB', {
    month: 'long', year: 'numeric'
  })

  return (
    <div className="bg-white border border-neutral-200 rounded-lg shadow-lg p-3 min-w-48">
      <p className="font-medium text-neutral-900 mb-2">{monthYear}</p>
      <div className="flex items-center justify-between gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: FIXED_COLOR }} />
          <span className="text-neutral-600">Fixed</span>
        </div>
        <span className="font-medium text-neutral-900">{row.fixed.toFixed(1)}p/kWh</span>
      </div>
      <div className="flex items-center justify-between gap-4 text-sm mt-1">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: ALT_COLOR }} />
          <span className="text-neutral-600">{tariffLabel}</span>
        </div>
        <span className="font-medium text-neutral-900">{row.alt.toFixed(1)}p/kWh</span>
      </div>
      <p className="text-[10px] text-neutral-500 mt-2">
        Effective unit rate = total cost ÷ kWh consumed (standing charges included)
      </p>
    </div>
  )
}

function CostTooltip({ active, payload, label, tariffLabel }) {
  if (!active || !payload?.length) return null
  const row = payload[0]?.payload
  if (!row) return null

  const monthYear = new Date(label).toLocaleDateString('en-GB', {
    month: 'long', year: 'numeric'
  })
  const delta = row.fixed - row.alt
  const deltaLabel = delta >= 0
    ? `£${delta.toFixed(0)} cheaper on ${tariffLabel}`
    : `£${Math.abs(delta).toFixed(0)} dearer on ${tariffLabel}`

  return (
    <div className="bg-white border border-neutral-200 rounded-lg shadow-lg p-3 min-w-48">
      <p className="font-medium text-neutral-900 mb-2">{monthYear}</p>
      <div className="flex items-center justify-between gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: FIXED_COLOR }} />
          <span className="text-neutral-600">Fixed</span>
        </div>
        <span className="font-medium text-neutral-900">£{row.fixed.toFixed(0)}</span>
      </div>
      <div className="flex items-center justify-between gap-4 text-sm mt-1">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: ALT_COLOR }} />
          <span className="text-neutral-600">{tariffLabel}</span>
        </div>
        <span className="font-medium text-neutral-900">£{row.alt.toFixed(0)}</span>
      </div>
      <div className="border-t border-neutral-200 mt-2 pt-2 text-sm">
        <span className={delta >= 0 ? 'text-green-600' : 'text-red-600'}>
          {deltaLabel}
        </span>
      </div>
      {row.coverage_pct < 100 && (
        <p className="text-xs text-neutral-500 mt-2">
          Coverage: {row.coverage_pct.toFixed(1)}%
        </p>
      )}
    </div>
  )
}

function KwhTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  const row = payload[0]?.payload
  if (!row) return null

  const monthYear = new Date(label).toLocaleDateString('en-GB', {
    month: 'long', year: 'numeric'
  })

  return (
    <div className="bg-white border border-neutral-200 rounded-lg shadow-lg p-3 min-w-48">
      <p className="font-medium text-neutral-900 mb-2">{monthYear}</p>
      <div className="flex items-center justify-between gap-4 text-sm">
        <span className="text-neutral-600">Consumption</span>
        <span className="font-medium text-neutral-900">
          {row.kwh.toFixed(0)} kWh
        </span>
      </div>
      {row.coverage_pct < 100 && (
        <p className="text-xs text-neutral-500 mt-2">
          Coverage: {row.coverage_pct.toFixed(1)}%
        </p>
      )}
    </div>
  )
}

export default function AgileComparisonChart({
  data,
  isLoading,
  tariff = 'agile',
  tariffLabel = 'Agile',
  onTariffChange,
}) {
  const [mode, setMode] = useState('cost') // 'cost' | 'unit' | 'kwh'

  const chartData = useMemo(() => {
    if (!data?.length) return []
    return data.map(row => {
      const kwh = parseFloat(row.kwh_consumed ?? 0)
      const fixedPence = row.fixed_total_excl_vat_pence ?? 0
      const altPence = row.alt_total_excl_vat_pence ?? 0

      let fixed, alt
      if (mode === 'unit') {
        // Effective p/kWh = total excl-VAT pence ÷ kWh (standing charges included)
        fixed = kwh > 0 ? fixedPence / kwh : 0
        alt = kwh > 0 ? altPence / kwh : 0
      } else {
        // Cost mode: £ excl VAT
        fixed = fixedPence / 100
        alt = altPence / 100
      }

      return {
        month: row.month,
        fixed,
        alt,
        kwh,
        coverage_pct: parseFloat(row.coverage_pct ?? 0),
        savingBand: mode !== 'kwh' && alt < fixed ? [alt, fixed] : null,
        deficitBand: mode !== 'kwh' && alt > fixed ? [fixed, alt] : null,
      }
    })
  }, [data, mode])

  const title = `Fixed vs ${tariffLabel} — Monthly Comparison`

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[360px] w-full" />
        </CardContent>
      </Card>
    )
  }

  if (!chartData.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[360px] text-neutral-500">
            No comparison data available
          </div>
        </CardContent>
      </Card>
    )
  }

  const isKwh = mode === 'kwh'
  const yFormatter = mode === 'cost' ? formatCurrency : mode === 'unit' ? formatUnit : formatKwh
  const activeTooltip = mode === 'cost'
    ? <CostTooltip tariffLabel={tariffLabel} />
    : mode === 'unit'
      ? <UnitRateTooltip tariffLabel={tariffLabel} />
      : <KwhTooltip />

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <CardTitle>{title}</CardTitle>
          <div className="flex items-center gap-3">
            {onTariffChange && (
              <TariffSelector value={tariff} onChange={onTariffChange} />
            )}
            <ToggleGroup
              type="single"
              value={mode}
              onValueChange={(value) => value && setMode(value)}
              size="sm"
            >
              <ToggleGroupItem value="cost" className="text-xs px-3">
                Cost
              </ToggleGroupItem>
              <ToggleGroupItem value="unit" className="text-xs px-3">
                p/kWh
              </ToggleGroupItem>
              <ToggleGroupItem value="kwh" className="text-xs px-3">
                kWh
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>
        <p className="text-xs text-neutral-500 mt-2">
          Counterfactual comparison at today's tariff rates applied to historical consumption.
          Does not model load shifting — battery analysis is separate.
        </p>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={360}>
          <ComposedChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="month"
              tickFormatter={formatMonth}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6b7280' }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6b7280' }}
              tickFormatter={yFormatter}
            />
            <Tooltip content={activeTooltip} />
            {!isKwh && (
              <Legend
                wrapperStyle={{ fontSize: '12px', paddingTop: '12px' }}
                iconType="line"
              />
            )}
            {isKwh ? (
              <Line
                type="monotone"
                dataKey="kwh"
                name="Consumption"
                stroke={ALT_COLOR}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
                isAnimationActive={false}
              />
            ) : (
              <>
                <Area
                  type="monotone"
                  dataKey="savingBand"
                  stroke="none"
                  fill={SAVING_COLOR}
                  fillOpacity={0.18}
                  isAnimationActive={false}
                  activeDot={false}
                  legendType="none"
                  connectNulls={false}
                />
                <Area
                  type="monotone"
                  dataKey="deficitBand"
                  stroke="none"
                  fill={DEFICIT_COLOR}
                  fillOpacity={0.18}
                  isAnimationActive={false}
                  activeDot={false}
                  legendType="none"
                  connectNulls={false}
                />
                <Line
                  type="monotone"
                  dataKey="fixed"
                  name="Fixed"
                  stroke={FIXED_COLOR}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="alt"
                  name={tariffLabel}
                  stroke={ALT_COLOR}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  isAnimationActive={false}
                />
              </>
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}
